<template>
  <div>
    <div class="row">
      <div class="col">
        <div class="subtitle">
          <h2>Create task metrics</h2>
          <p>Key in the task metrics and set a goal for it</p>
        </div>
      </div>
    </div>

    <form v-on:submit.prevent="submitForm">
      <div class="form-row">
        <div class="col col-md-6 mb-3">
          <label class="wfh-label">Set A Goal Name</label>
          <Selector
              title="goal"
              :options="goal_names_options"
              :selected="goal.name"
              v-on:resetValue="resetGoalName"
              v-on:selectItem="(val, id) => {goal.name = val; goal.goal_name_id = id;}"
              v-on:updateLabel="updateGoal"
              v-on:deleteItem="deleteGoal"
          />
        </div>
        <div class="col col-md-6 mb-3">
          <label class="wfh-label">Goal Target Number</label>
          <input type="number" class="form-control wfh-input-border wfh-input-padding wfh-input-text border-right-0"
                 v-model="goal.target_number" autocomplete="off" required>
        </div>
      </div>

      <div class="form-row">
        <div class="col col-md-6 mb-3">
          <label class="wfh-label">Choose a Label</label>
          <Selector
              :required="false"
              :options="labelOptions"
              :selected="goal.label_id"
              v-on:resetValue="goal.label_id = null"
              v-on:selectItem="(val, id) => (goal.label_id = id)"
              v-on:updateLabel="updateLabel"
              v-on:deleteItem="deleteLabel"
          />
        </div>
        <div class="col col-md-6 mb-3">
          <label class="wfh-label">Assign Task To</label>
          <div class="wfh-v-select-override">
            <v-select
                :options="staffs"
                :multiple="true"
                @search="executeSearch"
                label="name"
                :filterable="false"
                v-model="goal.users"
            >
              <template #search="{attributes, events}">
                <input
                    style="border: none;"
                    class="vs__search"
                    :required="goal.users.length === 0"
                    v-bind="attributes"
                    v-on="events"
                />
              </template>
              <template slot="option" slot-scope="option">
                <div class="d-center">
                  {{ option.name }}
                </div>
              </template>
            </v-select>
          </div>
        </div>
      </div>

      <div class="form-row">
        <div class="col col-md-6 mb-3">
          <label class="wfh-label">Assign Task To Team</label>
          <div class="wfh-v-select-override">
            <v-select
                :options="team_options"
                :filterable="false"
                v-model="goal.team_id"
                :reduce="item => item.code"
            >
              <template #search="{attributes, events}">
                <input
                    style="border: none;"
                    class="vs__search"
                    v-bind="attributes"
                    v-on="events"
                />
              </template>
            </v-select>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-auto ml-auto">
          <button type="button"  class="btn btn-link font-weight-bold text-grey-02 pr-0" @click="addNewMetric">
            <AddPlusIcon/>
            Add Metric
          </button>
        </div>
      </div>

      <div class="form-row" v-for="(metric, key) in goal.metrics">
        <div class="col col-md-6 mb-3">
          <label class="wfh-label">Metric Name</label>
          <input type="text" class="form-control wfh-input-border wfh-input-padding wfh-input-text border-right-0"
                 v-model="metric.name" autocomplete="off" :disabled="key === 0" required>
        </div>
        <div class="col col-md-6 mb-3">
          <label class="wfh-label">Metric Number Hit</label>
          <input type="number" class="form-control wfh-input-border wfh-input-padding wfh-input-text border-right-0"
                 v-model="metric.number_hits" autocomplete="off" required>
        </div>

        <div class="col-12 text-right" v-if="key > 0">
          <button
              type="button"
              class="btn btn-link font-weight-bold text-grey pr-0"
              @click="removeMetric(key)"
          >
            Remove
          </button>
        </div>
      </div>

      <MetricsPreview :metrics="goal.metrics" :goal="{name: goal.name, number: goal.target_number}"/>

      <div class="row">
        <div class="col-5 mx-auto">
          <button type="submit" class="btn btn-block wfh-btn-color-primary wfh-btn-large position-relative"
                  :disabled="is_loading">
            <span
                class="spinner-border position-absolute"
                role="status"
                style="left: 16px; top: 14px"
                v-if="is_loading"
            >
                <span class="sr-only">Loading...</span>
            </span>
            Done
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Selector from '../../Misc/CustomSelector/Selector';
import MetricsPreview from './MetricsPreview';
import vSelect from 'vue-select';
import _ from 'lodash';
import AddPlusIcon from '../../../icons/AddPlusIcon';

export default {
  components: { AddPlusIcon, MetricsPreview, Selector, vSelect },

  props: ['user_company', 'goal_id'],

  data () {
    return {
      goal: {
        name: '',
        target_number: null,
        label: null,
        goal_name_id: null,
        team_id: null,
        completed_at: null,
        metrics: [],
        users: [],
      },
      goal_names_options: [],
      label_options: [],
      is_loading: false,
      teams: [],
      staffs: [],
      errors: [],
    };
  },

  watch: {
    user_company () {
      if (!!this.user_company) {
        this.fetchCompanyGoalNames();

        if (this.goal_id) {
          this.fetchGoal();
        }

        this.label_options = !!this.user_company.labels ? this.user_company.labels.map((item, key) => {
          return {
            name: item,
            id: key,
          };
        }) : [];
      }
    },

    'goal.users' () {
      this.teams = [];
      this.goal.users.forEach(item => {
        this.getUserGroup(item.id);
      });
    },

    'goal.goal_name_id' () {
      if (!this.goal_id) {
        this.getPreviousGoalMetrics();
      }
    },

    currentUser () {
      this.addCurrentUserToTaskUsers();
    },
  },

  computed: {
    currentUser () {
      return this.$store.state.currentUser;
    },

    team_options () {
      const teams = this.teams.map(item => {
        return {
          label: item.name,
          code: item.id,
        };
      });

      if (teams.length === 1 && !this.goal_id) {
        this.goal.team_id = teams[0].code;
      }

      return teams;
    },

    labelOptions () {
      return this.$store.state.labelOptions;
    },
  },

  methods: {
    getPreviousGoalMetrics: _.debounce(async function () {
      const res = await axios.get(
          `/companies/${this.user_company.id}/get-goal-by-name?goal=${this.goal.goal_name_id}`);
      const data = res.data.data;

      if (!!data) {
        this.goal.metrics = [];
        data.metrics.forEach(item => {
          this.goal.metrics.push({
            name: item.name,
            number_hits: null,
          });
        });
      }
    }, 500),

    executeSearch: _.debounce(async function (queryString) {
      await this.getStaffs(queryString);
    }, 500),

    async getStaffs (queryString) {
      const res = await axios.get('/team-members' + (queryString ? `?q=${queryString}` : ''));
      this.staffs = res.data.data;
    },

    addNewMetric () {
      this.goal.metrics.push({
        name: null,
        number_hits: null,
      });
    },

    removeMetric (key) {
      this.goal.metrics.splice(key, 1);
    },

    async updateGoal (val, id) {
      if (!!id) {
        const res = await axios.put(`/companies/${this.user_company.id}/goal-names/${id}`, {
          name: val,
        });
        const data = res.data.data;

        this.goal_names_options = this.goal_names_options.map(item => {
          if (item.id === data.id) {
            item.name = data.name;
          }

          return item;
        });
      } else {
        const res = await axios.post(`/companies/${this.user_company.id}/goal-names`, {
          name: val,
        });

        this.goal_names_options = [
          ...this.goal_names_options,
          res.data.data,
        ];
      }
    },

    async deleteGoal (id) {
      await axios.delete(`/companies/${this.user_company.id}/goal-names/${id}`);

      this.goal_names_options = this.goal_names_options.filter(item => {
        return item.id !== id;
      });
    },

    async updateLabel (val, labelId) {
      let label = null;
      let isNewLabel = true;

      if (labelId) {
        const res = await axios.patch(`/company/${this.currentUser.company.id}/labels/${labelId}`, { label: val });
        label = res.data.data;
      } else {
        const res = await axios.post(`/company/${this.currentUser.company.id}/labels`, { label: val });
        label = res.data.data;
      }

      this.$store.state.labelOptions = this.labelOptions.map((value) => {
        if (label.id === value.id) {
          isNewLabel = false;
          return label;
        }

        return value;
      });

      if (isNewLabel) {
        this.$store.state.labelOptions.push(label);
      }
    },

    async deleteLabel (labelId) {
      await axios.delete(`/company/${this.currentUser.company.id}/labels/${labelId}`);

      this.$store.state.labelOptions = this.labelOptions.filter((value) => {
        return value.id !== labelId;
      });
    },

    async submitForm () {
      this.is_loading = true;

      try {
        if (!!this.goal_id) {
          const res = await axios.put(`/companies/${this.user_company.id}/goals/${this.goal_id}`, {
            name: this.goal.name,
            target_number: this.goal.target_number,
            metrics: this.goal.metrics,
            goal_name_id: this.goal.goal_name_id,
            company_id: this.user_company.id,
            label_id: this.goal.label_id,
            group_id: this.goal.team_id,
            assigned_to: this.goal.users.length > 0 ? this.goal.users.map(assignee => {
              return assignee.id;
            }) : [this.currentUser.id],
          });
        } else {
          const res = await axios.post(`/companies/${this.user_company.id}/goals`, {
            name: this.goal.name,
            target_number: this.goal.target_number,
            metrics: this.goal.metrics,
            goal_name_id: this.goal.goal_name_id,
            company_id: this.user_company.id,
            label_id: this.goal.label_id,
            group_id: this.goal.team_id,
            assigned_to: this.goal.users.length > 0 ? this.goal.users.map(assignee => {
              return assignee.id;
            }) : [this.currentUser.id],
          });
        }

        window.location.href = '/dashboard';
        this.is_loading = false;
      } catch (e) {
        this.is_loading = false;
      }
    },

    async fetchCompanyGoalNames () {
      const res = await axios.get(`/companies/${this.user_company.id}/goal-names`);
      this.goal_names_options = res.data.data;
    },

    resetGoalName () {
      this.goal.name = null;
      this.goal.goal_name_id = null;
    },

    addCurrentUserToTaskUsers () {
      if (!this.currentUser.id) return;

      if (this.goal.users.length === 0) {
        this.goal.users.push(this.currentUser);
      }

      this.getUserGroup(this.currentUser.id);
    },

    async getUserGroup (userId) {
      await axios.get(`/users/${userId}/groups`).then(data => {
        data.data.data.forEach(item => {
          const group = _.find(this.teams, {
            id: item.id,
          });

          if (!group) {
            this.teams.push(item);
          }
        });
      });
    },

    async fetchGoal () {
      this.goal.metrics = [];
      const res = await axios.get(`/companies/${this.user_company.id}/goals/${this.goal_id}`);
      const data = res.data.data;

      const metrics = data.metrics;

      this.goal.label_id = data.labels[0].id;
      this.goal.target_number = data.target_number;
      this.goal.name = data.goal_name_id;
      this.goal.goal_name_id = data.goal_name_id;
      this.goal.team_id = data.group ? data.group.id : null;
      this.goal.users = data.users;

      metrics.forEach(item => {
        this.goal.metrics.push({
          name: item.name,
          number_hits: item.number_hits,
          id: item.id,
        });
      });

    },
  },

  mounted () {
    if (this.goal.metrics.length === 0) {
      this.goal.metrics.push({
        name: 'Completed',
        number_hits: null,
      });
    }

    this.getStaffs();
    this.addCurrentUserToTaskUsers();
  },
};
</script>
